// The sign-in rules, ported from v1 (js/auth.js) and SPEC.md §3:
//   - every day: email + password, then the 6-digit authenticator code if the person has one
//   - first time or forgot: an emailed code, then pick a password (10+ characters)
//   - admins must set up an authenticator; with "2-step for everyone" on, so must techs
//   - someone who set one up but skipped the code this time gets no access until they type it
//   - only people on the team get in; a removed member sees "Access removed"
//   - with no signal, the saved team row lets a tech in
import { useSyncExternalStore } from "react";
import type { AuthApi, Member, Session, Enrolment } from "./auth-api";

export type AuthState =
  | { step: "loading" }
  | { step: "signed-out"; message?: string }
  | { step: "email-code"; email: string; message?: string }
  | { step: "not-on-team"; email: string }
  | { step: "removed" }
  | { step: "mfa-code"; factorId: string; message?: string }
  | { step: "mfa-enrol"; enrolment: Enrolment; why: string; optional: boolean; message?: string }
  | { step: "set-password"; change: boolean; message?: string }
  | { step: "error"; message: string }
  | { step: "ready"; session: Session; member: Member; mfaOn: boolean; mfaRequired: boolean };

const MEMBER_KEY = (uid: string) => `sr2-member-${uid}`;

export class AuthFlow {
  private state: AuthState = { step: "loading" };
  private session: Session | null = null;
  private member: Member | null = null;
  private needPassword = false;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly api: AuthApi) {}

  get = (): AuthState => this.state;
  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };
  private set(s: AuthState) {
    this.state = s;
    for (const l of this.listeners) l();
  }

  /** At boot: a saved session skips the password; otherwise show the sign-in screen. */
  async start(): Promise<void> {
    this.api.onSessionFromLink((s, needPassword) => {
      if (this.session) return;
      this.session = s;
      this.needPassword = needPassword;
      void this.continueAfterSession();
    });
    this.session = await this.api.getSession();
    if (!this.session) return this.set({ step: "signed-out" });
    await this.continueAfterSession();
  }

  async signIn(email: string, password: string): Promise<void> {
    email = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) return this.set({ step: "signed-out", message: "Enter your email address." });
    if (!password) return this.set({ step: "signed-out", message: "Enter your password." });
    const r = await this.api.signInWithPassword(email, password);
    if (!r.ok) {
      const m = /invalid login|credentials/i.test(r.message)
        ? "Email or password doesn't match. First time here, or forgot it? Use the button below."
        : /rate|too many/i.test(r.message)
          ? "Too many tries. Wait a few minutes, then try again."
          : r.message;
      return this.set({ step: "signed-out", message: m });
    }
    rememberEmail(email);
    this.session = r.value;
    await this.continueAfterSession();
  }

  startEmailCode(email = ""): void {
    this.set({ step: "email-code", email: email.trim() || lastEmail() });
  }

  async sendEmailCode(email: string): Promise<void> {
    email = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) return this.set({ step: "email-code", email, message: "Enter your email address." });
    const r = await this.api.sendEmailCode(email);
    if (!r.ok) {
      const m = /rate limit/i.test(r.message) ? "Too many emails for now. Use the newest one already in your inbox, or wait about an hour." : r.message;
      return this.set({ step: "email-code", email, message: m });
    }
    rememberEmail(email);
    this.set({ step: "email-code", email, message: `We sent a code to ${email}. Type it below, or tap the link in the email.` });
  }

  async verifyEmailCode(email: string, code: string): Promise<void> {
    if (!code.trim()) return;
    const r = await this.api.verifyEmailCode(email, code.trim());
    if (!r.ok) return this.set({ step: "email-code", email, message: "That code didn't work. Check it, or send a new one." });
    this.session = r.value;
    this.needPassword = true;
    await this.continueAfterSession();
  }

  backToSignIn(): void {
    this.set({ step: "signed-out" });
  }

  private async continueAfterSession(): Promise<void> {
    const s = this.session;
    if (!s) return this.set({ step: "signed-out" });
    this.set({ step: "loading" });
    const m = await this.loadMemberWithCache(s.userId);
    if (!m) return this.set({ step: "not-on-team", email: s.email });
    this.member = m;
    if (m.role === "removed") return this.set({ step: "removed" });
    try {
      const gated = await this.mfaGate();
      if (gated) return; // waiting on a code or an enrolment
    } catch (e) {
      return this.set({ step: "error", message: `The check for your authenticator app didn't go through (${e instanceof Error ? e.message : "no connection"}). Check your signal, then try again.` });
    }
    if (this.needPassword || !s.pwSet) return this.set({ step: "set-password", change: false });
    await this.finish();
  }

  // Returns true when the flow is now waiting on the person (code or enrolment).
  private async mfaGate(): Promise<boolean> {
    const [aal, factors, everyone] = await Promise.all([this.api.assuranceLevel(), this.api.listFactors(), this.api.mfaEveryone()]);
    if (aal === "aal2") return false;
    const verified = factors.find((f) => f.status === "verified");
    if (verified) {
      this.set({ step: "mfa-code", factorId: verified.id });
      return true;
    }
    const required = this.member?.role === "owner" || everyone;
    if (required) {
      await this.beginEnrol(false);
      return true;
    }
    return false;
  }

  async verifyMfa(factorId: string, code: string): Promise<void> {
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6) return this.set({ step: "mfa-code", factorId, message: "Type all 6 digits." });
    const r = await this.api.verifyCode(factorId, digits);
    if (!r.ok) return this.set({ step: "mfa-code", factorId, message: "That code didn't match. Codes change every 30 seconds: use the one showing now, and check your phone's clock is set automatically." });
    if (this.session) this.session = { ...this.session, aal: "aal2" };
    if (this.needPassword || !this.session?.pwSet) return this.set({ step: "set-password", change: false });
    await this.finish();
  }

  async beginEnrol(optional: boolean): Promise<void> {
    for (const f of await this.api.listFactors()) if (f.status !== "verified") await this.api.unenrol(f.id);
    const r = await this.api.startEnrol();
    if (!r.ok) return this.set({ step: "error", message: r.message || "Couldn't start the authenticator setup. Try again in a minute." });
    const why = optional
      ? "A code from your phone on top of your password, so a stolen password alone can't get in."
      : this.member?.role === "owner"
        ? "Admins see costs and the team, so admin sign-in needs a code from your phone too."
        : "Your team requires it: a code from your phone on top of your password.";
    this.set({ step: "mfa-enrol", enrolment: r.value, why, optional });
  }

  async finishEnrol(enrolment: Enrolment, code: string, optional: boolean): Promise<void> {
    const why = this.state.step === "mfa-enrol" ? this.state.why : "";
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6) return this.set({ step: "mfa-enrol", enrolment, why, optional, message: "Type all 6 digits." });
    const r = await this.api.verifyCode(enrolment.factorId, digits);
    if (!r.ok) return this.set({ step: "mfa-enrol", enrolment, why, optional, message: "That code didn't match. Use the code showing now; they change every 30 seconds." });
    if (this.session) this.session = { ...this.session, aal: "aal2" };
    if (optional) return this.finish();
    if (this.needPassword || !this.session?.pwSet) return this.set({ step: "set-password", change: false });
    await this.finish();
  }

  async skipEnrol(enrolment: Enrolment): Promise<void> {
    await this.api.unenrol(enrolment.factorId);
    await this.finish();
  }

  async savePassword(a: string, b: string, change: boolean): Promise<void> {
    if (a.length < 10) return this.set({ step: "set-password", change, message: "At least 10 characters." });
    if (a !== b) return this.set({ step: "set-password", change, message: "The two don't match." });
    const r = await this.api.setPassword(a);
    if (!r.ok) {
      const m = /different from the old/i.test(r.message)
        ? "That's your current password. Pick a new one."
        : /weak|pwned|leaked|short/i.test(r.message)
          ? "That password is too easy to guess (it's been in a data leak). Pick another."
          : /aal2|reauth/i.test(r.message)
            ? "Sign out and back in, then try again."
            : r.message;
      return this.set({ step: "set-password", change, message: m });
    }
    if (this.session) this.session = { ...this.session, pwSet: true };
    this.needPassword = false;
    await this.finish();
  }

  startChangePassword(): void {
    this.set({ step: "set-password", change: true });
  }

  async turnMfaOff(): Promise<string | null> {
    const verified = (await this.api.listFactors()).find((f) => f.status === "verified");
    if (!verified) return null;
    const r = await this.api.unenrol(verified.id);
    if (!r.ok) return r.message;
    await this.finish();
    return null;
  }

  private async finish(): Promise<void> {
    const s = this.session;
    if (!s) return this.set({ step: "signed-out" });
    // the role can change once 2-step is done (an admin without the code works as a tech)
    this.member = (await this.loadMemberWithCache(s.userId)) ?? this.member;
    if (!this.member) return this.set({ step: "not-on-team", email: s.email });
    const factors = await this.api.listFactors().catch(() => []);
    const everyone = await this.api.mfaEveryone().catch(() => false);
    const mfaOn = factors.some((f) => f.status === "verified");
    this.set({ step: "ready", session: s, member: this.member, mfaOn, mfaRequired: this.member.role === "owner" || everyone });
    void this.api.touchSeen();
  }

  async signOut(): Promise<void> {
    await this.api.signOut();
    this.session = null;
    this.member = null;
    this.needPassword = false;
    this.set({ step: "signed-out" });
  }

  // The team row is kept on this device too, so a tech with no signal still gets in (read-only, SPEC §3.3).
  private async loadMemberWithCache(uid: string): Promise<Member | null> {
    const r = await this.api.loadMember(uid);
    if (r === "offline") {
      try {
        const cached: unknown = JSON.parse(localStorage.getItem(MEMBER_KEY(uid)) ?? "null");
        return cached && typeof cached === "object" ? (cached as Member) : null;
      } catch {
        return null;
      }
    }
    if (r) {
      try {
        localStorage.setItem(MEMBER_KEY(uid), JSON.stringify(r));
      } catch {
        // storage blocked; the sign-in still works online
      }
    }
    return r;
  }
}

export function lastEmail(): string {
  try {
    return localStorage.getItem("sr2-last-email") ?? "";
  } catch {
    return "";
  }
}
function rememberEmail(e: string) {
  try {
    localStorage.setItem("sr2-last-email", e);
  } catch {
    // fine
  }
}

export function useAuthState(flow: AuthFlow): AuthState {
  return useSyncExternalStore(flow.subscribe, flow.get, flow.get);
}
